import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { IconButtonWithText, MixerLoader } from '../../components/Common';
import { useHttpGetProjectWithClientUserBranch } from '../../hooks';
import useHttpGetProjectsByRoleOMX from '../../hooks/project/projectApi/useHttpGetProjectsByRoleOMX';
import PageNotFound from '../PageNotFound';
import { ProjectStatusBadge } from '../Projects/TableView';
import { ProjectTabs, ProjectTabType, ProjectOverviewTab } from '../../components/Projects';
import { selectAuth } from '../../store/slices/auth.slice';
import { RoleTypeEnum } from '../../commonTypes/user.types';

interface ProjectHeaderProps {
    projectName: string;
    clientName: string;
    projectStatus?: string;
    projectId: string;
    canEdit: boolean;
}

const ProjectHeader = ({ projectName, clientName, projectStatus, projectId, canEdit }: ProjectHeaderProps) => {
    const navigate = useNavigate();

    return (
        <div className="project-header">
            <button className="project-header__back" onClick={() => navigate('/projects')}>
                <img src="/svg/arrow-left-gray.svg" alt="Back" />
                BACK TO PROJECT LIST
            </button>

            <div className="project-header__content">
                <div className="project-header__info">
                    <h1>{projectName}</h1>
                    <div className="project-header__meta">
                        <p className="project-header__client">{clientName}</p>
                        {projectStatus && <ProjectStatusBadge status={projectStatus} />}
                    </div>
                </div>

                {canEdit && (
                    <IconButtonWithText
                        onClick={() => navigate(`/projects/${projectId}/edit`)}
                        text={'Edit project'}
                        iconSrc="/svg/pen.svg"
                        variant="text"
                    />
                )}
            </div>
        </div>
    );
};

interface ProjectPageProps {
    projectId: string;
}

const ProjectPage = ({ projectId }: ProjectPageProps) => {
    const authUser = useSelector(selectAuth);
    const [activeTab, setActiveTab] = useState<ProjectTabType>('overview');

    const canEdit = [RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN].includes(authUser.role.name);
    const canSeeAllTabs = [RoleTypeEnum.OWNER, RoleTypeEnum.ADMIN].includes(authUser.role.name);

    const adminData = useHttpGetProjectWithClientUserBranch(canEdit ? projectId : '');
    const ordinaryData = useHttpGetProjectsByRoleOMX();

    const ordinaryProject = useMemo(() => {
        if (!canEdit && ordinaryData.projects) {
            return ordinaryData.projects.find(p => p.id === projectId);
        }
        return undefined;
    }, [canEdit, ordinaryData.projects, projectId]);

    const project = canEdit ? adminData.project : ordinaryProject;
    const isReady = canEdit ? adminData.isReady : ordinaryData.isReady;
    const refreshProject = canEdit ? adminData.getProjects : () => {};

    if (!isReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    if (!project) {
        return <PageNotFound />;
    }

    const handleTabChange = (tab: ProjectTabType) => {
        setActiveTab(tab);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return <ProjectOverviewTab project={project as any} onRefresh={refreshProject} canEdit={canEdit} showRates={canEdit} />;
            // case 'files':
            //     return canSeeAllTabs ? <div>Files content</div> : null;
            // case 'audit-trail':
            //     return canSeeAllTabs ? <div>Audit Trail content</div> : null;
            // case 'timesheet':
            //     return canSeeAllTabs ? <div>Timesheet content</div> : null;
            // default:
            //     return null;
        }
    };

    return (
        <main className="project-page">
            <ProjectHeader
                projectName={project.name}
                clientName={project.clientName || ''}
                projectStatus={project.projectStatus}
                projectId={project.id}
                canEdit={canEdit}
            />

            {canSeeAllTabs && <ProjectTabs activeTab={activeTab} onTabChange={handleTabChange} />}

            <div className="project-page__content">{renderTabContent()}</div>
        </main>
    );
};

const ProjectPageWrapper = () => {
    const { id } = useParams();

    if (!id) {
        return <PageNotFound />;
    }

    return <ProjectPage projectId={id} />;
};

export default ProjectPageWrapper;
